import { useEffect, useState } from 'react';

/** Tailwind's `md` breakpoint — below this we switch to the phone layouts. */
export const MOBILE_QUERY = '(max-width: 767px)';

export function useMediaQuery(query: string): boolean {
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState<boolean>(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export const useIsMobile = () => useMediaQuery(MOBILE_QUERY);

/**
 * True while the on-screen keyboard is (very likely) open. Uses the
 * VisualViewport API: when the keyboard appears the visual viewport shrinks
 * noticeably while the layout viewport (window.innerHeight on iOS) does not.
 * Lets us hide fixed bottom chrome (tab bar / FAB) so it never floats over
 * the keyboard or covers the field being typed into.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    if (!coarse) return;
    const check = () => {
      const delta = window.innerHeight - vv.height;
      setOpen(delta > 140);
    };
    check();
    vv.addEventListener('resize', check);
    vv.addEventListener('scroll', check);
    return () => {
      vv.removeEventListener('resize', check);
      vv.removeEventListener('scroll', check);
    };
  }, []);
  return open;
}
